/**
 * DocumentVisual - Container visual for document blocks
 *
 * Queries for resolved document data and recursively renders:
 * - Sections (with their nested children)
 * - Text blocks
 * - Headers
 * - Dividers
 * - Play references
 *
 * Pure visual component - no editor state or handlers.
 */

import { useResolvedDocument } from '@/hooks/UseResolvedDocument'
import { BlockRenderer } from '../../BlockRenderer'
import type { BlockVisualProps } from '../../Types'
import type { DocumentBlock } from '@/types/blocks'

export function DocumentVisual({ block }: BlockVisualProps) {
	// Type guard - check first but don't return yet
	const isDocument = block.type === 'document'
	const documentBlock = isDocument ? (block as DocumentBlock) : null

	// Query for resolved document data - must be called unconditionally
	// Pass null if not a document block to satisfy hook rules
	const { document, isLoading } = useResolvedDocument(
		documentBlock ? documentBlock.id : null
	)

	// Early return after hook call
	if (!isDocument) {
		console.error('DocumentVisual called with non-document block:', block.type)
		return null
	}

	// Loading state
	if (isLoading) {
		return (
			<div className="document-loading">
				<p style={{ color: 'var(--muted-foreground)' }}>Loading document...</p>
			</div>
		)
	}

	// No data state
	if (!document) {
		return (
			<div className="document-empty">
				<p style={{ color: 'var(--muted-foreground)' }}>Document data not available</p>
			</div>
		)
	}

	// Extract children from resolved document
	// ResolvedDocument has a 'children' property directly
	const children = document.children

	if (!children || children.length === 0) {
		return (
			<div className="document-empty">
				<p style={{ color: 'var(--muted-foreground)' }}>Document is empty</p>
			</div>
		)
	}

	// Render all children recursively
	// documentBlock is guaranteed to be non-null here due to early return above
	return (
		<div className="document-container" data-block-id={documentBlock!.id}>
			{children.map((child, index) => {
				// Extract the actual block from the discriminated union
				if (child.type === 'section') {
					// Embed section children so SectionVisual can access them
					const sectionWithChildren = {
						...child.section,
						properties: {
							...child.section.properties,
							__resolved_children: child.children,
						},
					}
					return <BlockRenderer key={child.section.id} block={sectionWithChildren} />
				}

				// For other types, render the block directly
				const childBlock = child.block
				return <BlockRenderer key={childBlock.id || `child-${index}`} block={childBlock} />
			})}
		</div>
	)
}
